import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import InfoPage from ".";
import { getReturnTo } from "../../utils/returnTo";
import { persistAuthResult } from "../../utils/authHelpers";

const HostLogin = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check for session_expired reason in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      // Clean up URL after showing message
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/api/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const json = await res.json();
      if (res.ok && json.ok) {
        try { persistAuthResult({ email, token: json.token, user: json.user }); } catch (_) {}
        
        // Check subscription status before allowing access to dashboard
        try {
          const subRes = await fetch('/api/access/subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const subData = await subRes.json();
          
          if (subRes.ok && subData.ok && subData.active && (subData.roles || []).includes('host')) {
            // User has active host subscription - allow dashboard access
            window.location.assign(getReturnTo() || '/host-dashboard');
          } else {
            // No active subscription - check if user has completed profile
            try {
              const profileRes = await fetch(`/api/host/profile?email=${encodeURIComponent(email)}`);
              const profileData = await profileRes.json();
              
              if (profileData.ok && profileData.profile && profileData.profile.host_profile_id) {
                // User has profile - skip to checkout page
                window.location.assign(`/host/checkout?email=${encodeURIComponent(email)}`);
              } else {
                // No profile - send to onboarding first
                window.location.assign(`/host/onboarding?email=${encodeURIComponent(email)}`);
              }
            } catch (profileError) {
              console.error('Profile check error:', profileError);
              // On error, default to onboarding
              window.location.assign(`/host/onboarding?email=${encodeURIComponent(email)}`);
            }
          }
        } catch (subError) {
          console.error('Subscription check error:', subError);
          // If subscription check fails, redirect to onboarding to be safe
          window.location.assign(`/host/onboarding?email=${encodeURIComponent(email)}`);
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (_) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const belowContent = (
    <>
      {/* Left side - Image */}
      <div className="login-left">
        <img src="/house.png" alt="Host" className="login-image" />
        <p className="login-welcome">Welcome back</p>
      </div>

      {/* Right side - Form */}
      <div className="login-right">
        <form onSubmit={handleSubmit} className="login-form">
          <h3 className="login-title">Host Login</h3>

          {sessionExpired && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              color: '#856404',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span role="img" aria-label="warning">⚠️</span>
              Your session has expired. Please log in again.
            </div>
          )}

          <div className="login-input-group">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="login-input" />
            <label className="login-label">Email Address</label>
          </div>

          <div className="login-input-group">
            <div className="login-password-wrapper">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="login-input login-input-password" />
              <button type="button" onClick={() => setShowPassword(s => !s)} aria-label="Toggle password visibility" aria-pressed={showPassword} className="login-password-toggle">
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>
            <label className="login-label">
              Password{' '}
              <small>
                <a href="/forgot-password" style={{ textDecoration: 'none', color: 'inherit' }}>
                  Forgot Password
                </a>
              </small>
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="login-submit">{isSubmitting ? 'Logging in...' : 'Login'}</button>

          <div className="login-signup-link-below">
            Not a member yet? <a href="/host-select">Signup</a>
          </div>

          {submitStatus === 'error' && (
            <p className="login-error">Invalid email or password.</p>
          )}
        </form>
      </div>
    </>
  );

  return <InfoPage title="Host Login" divider belowContent={belowContent} disableStamps />;
};

export default HostLogin;


